import { useAuthStore } from '../store/useAuthStore';
import {
  canAccessFeature,
  isAdmin,
  isAdminOrSecretary,
  asCollaboratorRole,
  type Feature,
  type CollaboratorRole,
} from '../services/auth/permissions';
import { useFeatureOverrides } from './queries/useFeatureOverrides';
import { EDITABLE_FEATURES } from '../services/featureOverrides/featureOverridesService';

const EDITABLE = new Set<Feature>(EDITABLE_FEATURES as readonly Feature[]);

/**
 * Returns the current user's role (typed as CollaboratorRole) or null.
 */
export function useCurrentRole(): CollaboratorRole | null {
  const roleString = useAuthStore((s) => s.collaborator?.role);
  return asCollaboratorRole(roleString);
}

/**
 * Returns whether the current user can access a specific feature.
 * Pour les features whitelistées dans EDITABLE_FEATURES, l'override runtime
 * (table feature_overrides) prime sur la valeur hardcodée. Sinon, fallback
 * sur RESTRICTED_FEATURES.
 *
 * @example
 *   const canCreate = useCanAccessFeature('dossiers_create');
 */
export function useCanAccessFeature(feature: Feature): boolean {
  const role = useCurrentRole();
  const { data: overrides } = useFeatureOverrides();

  if (EDITABLE.has(feature) && overrides) {
    const ov = overrides.find((o) => o.feature === feature);
    if (ov && role) return ov.allowed_roles.includes(role);
  }
  return canAccessFeature(role, feature);
}

/**
 * Returns true if the current user is admin.
 */
export function useIsAdmin(): boolean {
  const role = useCurrentRole();
  return isAdmin(role);
}

/**
 * Returns true if the current user is admin or secretary (assistant).
 */
export function useIsAdminOrSecretary(): boolean {
  const role = useCurrentRole();
  return isAdminOrSecretary(role);
}

/**
 * Returns a bundle of boolean flags for commonly-checked features.
 * Prefer this hook when a component needs more than 2–3 permission checks.
 *
 * @example
 *   const { canCreateDossiers, canManageFirm } = usePermissions();
 */
export function usePermissions() {
  const role = useCurrentRole();
  return {
    role,
    // ── Settings ───────────────────────────────────────────────
    canManageFirm: canAccessFeature(role, 'settings_firm'),
    canManageBranding: canAccessFeature(role, 'settings_branding'),
    canManageTeam: canAccessFeature(role, 'settings_team'),
    canManageCategories: canAccessFeature(role, 'settings_categories'),
    // ── Dossiers ───────────────────────────────────────────────
    canCreateDossiers: canAccessFeature(role, 'dossiers_create'),
    canDeleteDossiers: canAccessFeature(role, 'dossiers_delete'),
    canViewDossiers: canAccessFeature(role, 'dossiers_view'),
    canEditDossiers: canAccessFeature(role, 'dossiers_edit'),
    // ── Tickets ────────────────────────────────────────────────
    canViewTickets: canAccessFeature(role, 'tickets_view'),
    canAssignTickets: canAccessFeature(role, 'tickets_assign'),
    canRespondTickets: canAccessFeature(role, 'tickets_respond'),
    canCloseTickets: canAccessFeature(role, 'tickets_close'),
    // ── Facturation ────────────────────────────────────────────
    canCreateInvoices: canAccessFeature(role, 'invoices_create'),
    canSendInvoices: canAccessFeature(role, 'invoices_send'),
    canManageBilling: canAccessFeature(role, 'billing_management'),
    // ── AI ─────────────────────────────────────────────────────
    canUseAILegal: canAccessFeature(role, 'ai_legal_assistant'),
    // ── General ────────────────────────────────────────────────
    canViewDashboard: canAccessFeature(role, 'dashboard'),
    canViewNotifications: canAccessFeature(role, 'notifications_view'),
  };
}
